// My CRM Dashboard — cross-project relationship pipeline: real KPI counts,
// a pipeline table of every contact with their real follow-up status, and a
// Follow-ups Due sidebar. Replaces the old 3-stat-card + "Recent Contacts"
// (with "No details" placeholder rows) layout — see RelationshipPipelineTable
// for the live-data fix.
import { useState, useEffect, useCallback } from 'react'
import { Users, Building2, CheckSquare, TrendingUp, Upload, Plus } from 'lucide-react'
import { getMyContacts, getMyOrgs, getCrmTodos, getProjects } from '../../../services/api'
import type { CrmContact, CrmOrganisation, CrmTodo, Project } from '../../../types'
import PageHeader from '../../../components/ui/PageHeader'
import ContextualTip from '../../../components/ui/ContextualTip'
import RelationshipPipelineTable from '../../../components/crm/RelationshipPipelineTable'
import FollowUpsSidebar from '../../../components/crm/FollowUpsSidebar'
import QuickAddContactModal from '../../../components/crm/QuickAddContactModal'
import ImportContactsModal from '../../../components/crm/ImportContactsModal'
import TodoForm from '../../../components/crm/TodoForm'
import {
  contactsInPipelineCount,
  distinctProjectCount,
  dueFollowUps,
  overdueCount,
} from '../../../utils/crmPipeline'

export default function MyCrmDashboard() {
  const [contacts, setContacts] = useState<CrmContact[]>([])
  const [orgs, setOrgs] = useState<CrmOrganisation[]>([])
  const [todos, setTodos] = useState<CrmTodo[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddContact, setShowAddContact] = useState(false)
  const [showImportCsv, setShowImportCsv] = useState(false)
  const [showTodoForm, setShowTodoForm] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    return Promise.all([
      getMyContacts().catch(() => ({ contacts: [], count: 0 })),
      getMyOrgs().catch(() => ({ organisations: [], count: 0 })),
      getCrmTodos().catch(() => ({ todos: [], count: 0 })),
      getProjects().catch(() => []),
    ]).then(([c, o, t, p]) => {
      setContacts(c?.contacts || [])
      setOrgs(o?.organisations || [])
      setTodos(t?.todos || [])
      setProjects(Array.isArray(p) ? p : [])
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="h-24 bg-sky-500/10 rounded-lg" />
          <div className="h-24 bg-sky-500/10 rounded-lg" />
          <div className="h-24 bg-sky-500/10 rounded-lg" />
          <div className="h-24 bg-sky-500/10 rounded-lg" />
        </div>
        <div className="h-64 bg-sky-500/10 rounded-lg" />
      </div>
    )
  }

  const contactsProjectCount = distinctProjectCount(contacts)
  const orgsProjectCount = distinctProjectCount(orgs)
  const due = dueFollowUps(todos)
  const overdue = overdueCount(todos)
  const inPipeline = contactsInPipelineCount(contacts, todos)

  const handleContactDeleted = (contactId: string) => {
    setContacts((prev) => prev.filter((c) => c.contact_id !== contactId))
  }

  const handleContactCreated = (contact: CrmContact) => {
    setContacts((prev) => [contact, ...prev])
    setShowAddContact(false)
  }

  const handleTodoCreated = (todo: CrmTodo) => {
    setTodos((prev) => [todo, ...prev])
    setShowTodoForm(false)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contacts & CRM"
        description="Your relationship pipeline across every project — people and organisations you're collaborating with, and who needs a follow-up."
        helpKey="crm"
        secondaryActions={
          <>
            <button
              onClick={() => setShowImportCsv(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-sky-200 text-sky-700 text-sm font-medium rounded-lg hover:bg-sky-500/10 transition-colors"
            >
              <Upload size={15} /> Import CSV
            </button>
            <button
              onClick={() => setShowTodoForm(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-sky-200 text-sky-700 text-sm font-medium rounded-lg hover:bg-sky-500/10 transition-colors"
            >
              <CheckSquare size={15} /> New to-do
            </button>
          </>
        }
        primaryAction={
          <button
            onClick={() => setShowAddContact(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors"
          >
            <Plus size={16} /> Add contact
          </button>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center shrink-0">
            <Users size={20} className="text-sky-600" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
            <p className="text-sm text-gray-500">Contacts</p>
            {contactsProjectCount > 0 && (
              <p className="text-xs text-gray-500">across {contactsProjectCount} project{contactsProjectCount === 1 ? '' : 's'}</p>
            )}
          </div>
        </div>

        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center shrink-0">
            <Building2 size={20} className="text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900">{orgs.length}</p>
            <p className="text-sm text-gray-500">Organisations</p>
            {orgsProjectCount > 0 && (
              <p className="text-xs text-gray-500">across {orgsProjectCount} project{orgsProjectCount === 1 ? '' : 's'}</p>
            )}
          </div>
        </div>

        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center shrink-0">
            <CheckSquare size={20} className="text-amber-600" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900">{due.length}</p>
            <p className="text-sm text-gray-500">Follow-ups due</p>
            {overdue > 0 && <p className="text-xs text-red-600 font-medium">{overdue} overdue</p>}
          </div>
        </div>

        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center shrink-0">
            <TrendingUp size={20} className="text-green-600" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900">{inPipeline}</p>
            <p className="text-sm text-gray-500">In pipeline</p>
            <p className="text-xs text-gray-500">have an open to-do</p>
          </div>
        </div>
      </div>

      {/* Pipeline table + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2">
          <RelationshipPipelineTable
            contacts={contacts}
            todos={todos}
            projects={projects}
            onContactDeleted={handleContactDeleted}
            onAddContact={() => setShowAddContact(true)}
          />
        </div>
        <div className="space-y-4">
          <FollowUpsSidebar todos={todos} onNewFollowUp={() => setShowTodoForm(true)} />
          <ContextualTip id="crm-pipeline-status">
            <strong>Status</strong> is computed from your real to-dos: a contact with an overdue open to-do
            needs follow-up, one with an open to-do due later is scheduled, and one with none shows no
            follow-up. Add a to-do to a contact to move them through the pipeline.
          </ContextualTip>
        </div>
      </div>

      {showAddContact && (
        <QuickAddContactModal
          projects={projects}
          onCreated={handleContactCreated}
          onClose={() => setShowAddContact(false)}
        />
      )}

      {showImportCsv && (
        <ImportContactsModal
          projects={projects}
          onImported={load}
          onClose={() => setShowImportCsv(false)}
        />
      )}

      {showTodoForm && (
        <TodoForm
          entityTypeId="user#general"
          onSave={handleTodoCreated}
          onCancel={() => setShowTodoForm(false)}
        />
      )}
    </div>
  )
}
